import * as React from "react";

// mui imports
import OutlinedInput from "@mui/material/OutlinedInput";
import { useTheme } from "@mui/system";

// custom imports
import CustomFormControl from "@/components/Form/FormControl/CustomFormControl";
import { InputFieldType } from "@/types";
import labelRequired from "@/utils/labelRequired";
import { secondaryStyles } from "../secondaryStyles";

export default function ControlledInput<T>({
  label = null,
  placeholder,
  icon,
  position = "start",
  type = "primary",
  value,
  setValue,
  required,
  disabled = false,
  inputType = "text",
  multilineProps = {},
  customHandleChange,
  styles,
  ...rest
}: ControlledInputProps<T>): ReturnType<React.FC> {
  const theme = useTheme();

  const handleOnChange = (event: React.ChangeEvent) => {
    if (inputType === "file")
      setValue?.((event?.target as HTMLInputElement)?.files);
    else setValue((event?.target as HTMLInputElement)?.value);
  };

  return (
    <CustomFormControl
      label={labelRequired(label, required)}
      disabled={disabled}
    >
      <OutlinedInput
        sx={{ ...secondaryStyles(type, theme), ...styles }}
        startAdornment={icon && position === "start" ? icon : null}
        endAdornment={icon && position === "end" ? icon : null}
        placeholder={placeholder}
        disabled={disabled}
        {...multilineProps}
        type={inputType}
        onChange={customHandleChange ?? handleOnChange}
        value={value}
        {...rest}
      />
    </CustomFormControl>
  );
}

export interface ControlledInputProps<T> {
  label?: string | null;
  placeholder?: string;
  icon?: JSX.Element;
  position?: string;
  type?: InputFieldType;
  required?: boolean;
  disabled?: boolean;
  value: Value<T>;
  setValue: any;
  inputType?: "text" | "password" | "number" | "file";
  defaultVal?: string;
  multilineProps?: Record<string, string | number | boolean>;
  styles?: any;
  customHandleChange?: any;
}

type Value<T> = T extends "text" | "password"
  ? string
  : T extends "number"
  ? number
  : T extends "file"
  ? File
  : string;
